import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { ENTITY_SELECT_LIST_PARAMS } from "@/core/api/use-paged-select-options";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import {
  EntitySearchableSelect,
  type SearchableSelectOption,
} from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import {
  DEFAULT_CURRENCY,
  majorToMinor,
  minorToMajor,
} from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type {
  QrCodeDto,
  SimulateWalletTransactionCompletedPayload,
} from "@/features/merchant/api/dto";
import { useMerchant } from "@/features/merchant/api/use-merchant";
import { useQrCodesByMerchant } from "@/features/merchant/api/use-qr-codes-by-merchant";
import { useSimulateSaleCompleted } from "@/features/merchant/api/use-simulate-sale-completed";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  createSimulateSaleSchema,
  type SimulateSaleFormValues,
} from "@/features/merchant/schemas/simulate-sale.schema";
import {
  useTransactionSelectOptions,
  useWalletSelectOptions,
} from "@/features/shared/api";

const EMPTY_VALUES: SimulateSaleFormValues = {
  walletTransactionId: "",
  customerWalletId: "",
  settlementWalletId: "",
  qrPayloadToken: "",
  amount: 0,
  fee: 0,
  currency: DEFAULT_CURRENCY,
  completedAt: "",
};

const joinLabel = (
  ...parts: Array<string | null | undefined>
): string =>
  parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(" · ");

/** Options QR : la valeur soumise est le `payloadToken`, pas l’UUID du QR. */
const mapQrToSelectOption = (
  qr: QrCodeDto
): SearchableSelectOption | null => {
  const token = qr.payloadToken?.trim();
  if (!token) return null;
  const label = joinLabel(qr.qrType, qr.status, token);
  return {
    value: token,
    label,
    description: label === token ? undefined : token,
    disabled: qr.status === "REVOKED" || qr.status === "EXPIRED",
  };
};

const toPayload = (
  values: SimulateSaleFormValues
): SimulateWalletTransactionCompletedPayload => {
  const currency = values.currency.trim();
  return {
    walletTransactionId: values.walletTransactionId.trim(),
    customerWalletId: values.customerWalletId.trim(),
    settlementWalletId: values.settlementWalletId.trim(),
    qrPayloadToken: values.qrPayloadToken.trim(),
    amountMinor: majorToMinor(values.amount, currency),
    feeAmountMinor: majorToMinor(values.fee, currency),
    currency,
    completedAt: values.completedAt.trim(),
  };
};

/**
 * Simulation de l'événement Kafka `wallet-transaction-completed` (annexe C) —
 * réservée au compte technique `flashpay-service` tant que Kafka n'est pas
 * câblé. Enregistre la vente marchande après un paiement QR.
 *
 * Identifiants : selects searchables (`POST /Transactions/all`,
 * `POST /Wallets/all`, `GET /QrCodes/merchant/{merchantId}`). Pas de champ
 * `merchantId` dans le contrat API — le marchand est déduit du QR. Sans
 * `merchantId` de page, le jeton QR reste un champ texte (pas de liste
 * globale de QR dans Postman).
 */
export function SimulateSaleDialog({
  open,
  onOpenChange,
  merchantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId?: string;
}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue.
  const schema = useMemo(() => createSimulateSaleSchema(), [i18n.language]);
  const simulate = useSimulateSaleCompleted();
  const [preview, setPreview] =
    useState<SimulateWalletTransactionCompletedPayload | null>(null);

  const { options: walletOptions, isLoading: walletsLoading } =
    useWalletSelectOptions(open);
  const { options: transactionOptions, isLoading: transactionsLoading } =
    useTransactionSelectOptions(open);
  const { data: merchant } = useMerchant(merchantId ?? "", open && Boolean(merchantId));
  const { data: qrCodes, isLoading: qrLoading } = useQrCodesByMerchant(
    merchantId ?? "",
    ENTITY_SELECT_LIST_PARAMS,
    open && Boolean(merchantId)
  );
  const qrOptions = useMemo(() => {
    const mapped: SearchableSelectOption[] = [];
    const seen = new Set<string>();
    for (const qr of qrCodes?.items ?? []) {
      const option = mapQrToSelectOption(qr);
      if (!option?.value || seen.has(option.value)) continue;
      seen.add(option.value);
      mapped.push(option);
    }
    return mapped;
  }, [qrCodes]);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  useEffect(() => {
    if (!open) {
      setPreview(null);
      form.reset(EMPTY_VALUES);
      return;
    }
    form.reset({
      ...EMPTY_VALUES,
      settlementWalletId: merchant?.settlementWalletId?.trim() ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset ciblé à l'ouverture.
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const settlementId = merchant?.settlementWalletId?.trim();
    if (settlementId) {
      form.setFieldValue("settlementWalletId", settlementId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- préremplissage du wallet de reversement du marchand.
  }, [open, merchant?.settlementWalletId]);

  const fieldError =
    (name: keyof SimulateSaleFormValues) =>
    (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const handleConfirm = () => {
    if (!preview) return;
    simulate.mutate(preview, {
      onSuccess: () => {
        notifSilence(tm("merchant.sales.feedback.simulated"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });
  };

  return (
    <RequireRole
      roles={[
        FLASHPAY_ROLES.SERVICE,
        FLASHPAY_ROLES.SUPERADMIN,
        FLASHPAY_ROLES.ADMIN,
      ]}
    >
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{tm("merchant.sales.simulate.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {tm("merchant.sales.simulate.description")}
          </p>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="walletTransactionId"
              validators={{ onChange: fieldError("walletTransactionId") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="walletTransactionId">
                    {tm("merchant.sales.simulate.walletTransactionId")}
                  </Label>
                  <EntitySearchableSelect
                    id="walletTransactionId"
                    options={transactionOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tm(
                      "merchant.sales.simulate.walletTransactionIdPlaceholder"
                    )}
                    isLoading={transactionsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="customerWalletId"
              validators={{ onChange: fieldError("customerWalletId") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="customerWalletId">
                    {tm("merchant.sales.simulate.customerWalletId")}
                  </Label>
                  <EntitySearchableSelect
                    id="customerWalletId"
                    options={walletOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tm(
                      "merchant.sales.simulate.customerWalletIdPlaceholder"
                    )}
                    isLoading={walletsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="settlementWalletId"
              validators={{ onChange: fieldError("settlementWalletId") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="settlementWalletId">
                    {tm("merchant.sales.simulate.settlementWalletId")}
                  </Label>
                  <EntitySearchableSelect
                    id="settlementWalletId"
                    options={walletOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tm(
                      "merchant.sales.simulate.settlementWalletIdPlaceholder"
                    )}
                    isLoading={walletsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="qrPayloadToken"
              validators={{ onChange: fieldError("qrPayloadToken") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="qrPayloadToken">
                    {tm("merchant.sales.simulate.qrPayloadToken")}
                  </Label>
                  {merchantId ? (
                    <EntitySearchableSelect
                      id="qrPayloadToken"
                      options={qrOptions}
                      value={field.state.value}
                      onValueChange={(value) => field.handleChange(value)}
                      placeholder={tm(
                        "merchant.sales.simulate.qrPayloadTokenPlaceholder"
                      )}
                      isLoading={qrLoading}
                    />
                  ) : (
                    <Input
                      id="qrPayloadToken"
                      className="notranslate"
                      placeholder={tm(
                        "merchant.sales.simulate.qrPayloadTokenPlaceholder"
                      )}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="amount" validators={{ onChange: fieldError("amount") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="simulate-amount">
                    {tm("merchant.sales.simulate.amount")}
                  </Label>
                  <Input
                    id="simulate-amount"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={tm("merchant.sales.simulate.amountPlaceholder")}
                    value={field.state.value || ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="fee" validators={{ onChange: fieldError("fee") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="simulate-fee">
                    {tm("merchant.sales.simulate.fee")}
                  </Label>
                  <Input
                    id="simulate-fee"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={tm("merchant.sales.simulate.feePlaceholder")}
                    value={field.state.value || ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="currency"
              validators={{ onChange: fieldError("currency") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="simulate-currency">
                    {tm("merchant.sales.simulate.currency")}
                  </Label>
                  <Input
                    id="simulate-currency"
                    className="notranslate"
                    placeholder={tm(
                      "merchant.sales.simulate.currencyPlaceholder"
                    )}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="completedAt"
              validators={{ onChange: fieldError("completedAt") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="simulate-completed-at">
                    {tm("merchant.sales.simulate.completedAt")}
                  </Label>
                  <Input
                    id="simulate-completed-at"
                    className="notranslate"
                    placeholder={tm(
                      "merchant.sales.simulate.completedAtPlaceholder"
                    )}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={simulate.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={simulate.isPending}>
                {simulate.isPending ? <ButtonSpinner /> : null}
                {tm("merchant.sales.simulate.submit")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview}
        title={t("common.mutationPreview.title")}
        description={t("common.mutationPreview.description")}
        t={tm}
        confirmLabel={tm("merchant.sales.simulate.submit")}
        pending={simulate.isPending}
        fees={
          preview
            ? [
                {
                  label: tm("merchant.sales.simulate.fee"),
                  amount: minorToMajor(
                    preview.feeAmountMinor,
                    preview.currency
                  ),
                  currency: preview.currency,
                },
              ]
            : undefined
        }
        total={
          preview
            ? {
                label: tm("merchant.sales.simulate.amount"),
                amount: minorToMajor(preview.amountMinor, preview.currency),
                currency: preview.currency,
              }
            : undefined
        }
        onConfirm={handleConfirm}
      />
    </RequireRole>
  );
}
